import React, { useEffect, useMemo, useState } from "react";
import { Link, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import uuid from "react-uuid";
import ApiService from "../../Services/apiService";
import AlertService from "../../Services/alertService";
import TranslationService from "../../Services/translationService";
import MainService from "../../Services/mainService";
import {
	addButtonSpinner,
	removeButtonSpinner,
	addPartialViewSpinner,
	removePartialViewSpinner,
} from "../../Store/Actions/spinner";
import { ERROR_KEY, VALID_IMAGE_TYPES_KEY } from "../../Constants/mainKeys";
import ArrowBackSvg from "./../../Components/Svg/arrowBackSvg";
import UploadButton from "./../../Components/UploadButton/uploadButton";
import CloseSvg from "./../../Components/Svg/closeSvg";
import ActionButton from "../../Components/ActionButton/actionButton";
import moment from "moment";

const buttonSpinnerId = uuid();

function DocumentSubscription(props) {
	const dispatch = useDispatch();
	const { language } = useSelector(state => state.language);
	const translations = useSelector(state => state.translation.translations);
	const [translationService, setTranslationService] = useState(null);
	const [doc, setDoc] = useState(null);
	const [uploadedFile, setUploadedFile] = useState(null);

	const { documentId } = props.match.params;
	const mainService = useMemo(() => new MainService(), []);

	useEffect(() => {
		setTranslationService(new TranslationService(translations));
	}, [translations]);

	useEffect(() => {
		if (documentId) getDocument(documentId);
	}, [documentId]);

	const getDocument = id => {
		const spinnerId = uuid();
		dispatch(addPartialViewSpinner(spinnerId));
		ApiService.getDocumentById(id)
			.then(res => {
				if (res && res.data) {
					if (res.data.signed) {
						props.history.push(`/${language}/document`);
						return;
					}
					setDoc(res.data)
				};
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
				props.history.push(`/${language}/document`);
				return;
			})
			.finally(() => dispatch(removePartialViewSpinner(spinnerId)));
	};

	const uploadFile = event => {
		const file = event.target.files[0];
		if (file) {
			const fileName = file.name;
			mainService
				.readFile(file, VALID_IMAGE_TYPES_KEY)
				.then(uploadedFile => {
					const currentFile = {
						file,
						fileName,
						uploadedFile,
					};
					setUploadedFile(currentFile);
				})
				.catch(error => {
					error &&
						AlertService.alert(
							AlertService.checkMessageType(error.respcode) || ERROR_KEY,
							error,
						);
				});
		}
	};

	const removeFile = () => {
		setUploadedFile(null);
	};

	const onSubmit = () => {
		if (!uploadedFile || !documentId) return;

		const formData = new FormData();
		formData.append("documentId", documentId);
		formData.append("DocumentSignedFile", uploadedFile?.file);

		dispatch(addButtonSpinner(buttonSpinnerId));
		ApiService.documentSign(formData)
			.then(() => {
				props.history.push(`/${language}/document`);
			})
			.catch(err => {
				AlertService.alert(
					AlertService.checkMessageType(err?.respcode) || ERROR_KEY,
					err,
				);
			})
			.finally(() => dispatch(removeButtonSpinner(buttonSpinnerId)));
	};

	const formatDate = value => {
		if (!value) return null;
		try {
			const d = new Date(value);
			return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
		} catch {
			return value;
		}
	};

	const hasTemplateData = d => {
		if (!d?.data || typeof d.data !== "string") return false;
		const s = d.data.trim();
		return s.length > 0 && s !== "null";
	};

	if (!translationService) return null;

	const t = (key, fallback) => translationService.translate(key) || fallback;

	return (
		<div className="container mt-4">
			<div className="row">
				<div className="col-12 d-flex justify-content-between align-items-center">
					<h2 className="content-title p-0">
						<Link
							to={`/${language}/document`}
							title={t("TR_BACK", "Back")}
							onClick={() => window.history.back()}>
							<ArrowBackSvg />
						</Link>

						{t("TR_SIGN", "Sign document")}
					</h2>
				</div>
			</div>
			<hr />
			{doc && (
				<div className="row">
					<div className="col-12 col-md-6">
						<h4 className="card-title mb-2 word-break-break-word">{doc.name}</h4>
						<div className="small text-muted mb-3 d-flex flex-wrap align-items-center">
							{doc.createDate && (
								<span className="mr-3">
									<strong>{t("TR_CREATE_DATE", "Created")}:</strong>{" "}
									{moment(
										MainService.convertUTCDateToLocalDate(
											new Date(doc.createDate),
										),
									).format("lll (HH:mm)")}
								</span>
							)}

						</div>
					</div>
				</div>
			)}
			<div className="row">
				<div className="col-12 col-md-6">
					<div className="form-group">
						{uploadedFile ? (
							<div className="upload-file-content">
								<div onClick={removeFile}>
									<CloseSvg />
								</div>
								<div
									style={{
										backgroundImage: `url(${uploadedFile?.uploadedFile})`,
										height: "300px",
										backgroundSize: "contain",
										backgroundPosition: "center",
										backgroundRepeat: "no-repeat",
										border: "1px solid #cccccc",
										borderRadius: "3px",
										overflow: "hidden",
									}}
									alt="signature-preview"
								/>
							</div>
						) : (
							<UploadButton
								textInputClasses="pl-100"
								labelValue=""
								text={`${t("TR_UPLOAD_FILE", "Upload file")} (${VALID_IMAGE_TYPES_KEY.join(", ")})`}
								clicked={event => uploadFile(event)}
							/>
						)}
					</div>
					<ActionButton
						type="button"
						spinnerId={buttonSpinnerId}
						clicked={onSubmit}
						disabled={!uploadedFile}
						className="mindalay--btn-default position-relative"
						name={t("TR_SIGN", "Sign")}
					/>
				</div>
			</div>
		</div>
	);
}

export default withRouter(DocumentSubscription);
